import { z } from 'zod'

import { lenientList, moneySchema } from '@/schemas/common'
import { isoDateTimeSchema } from '@/schemas/newsletter'
import { gatewayHandoffSchema } from '@/schemas/payments'
import { isoDateSchema } from '@/schemas/portfolio'

/**
 * Courses — `/api/v5/courses/`, in the app's own vocabulary.
 *
 * Wire shapes live in `@/api/schema` and are never restated here; this file
 * describes what the app holds AFTER `courseService`'s mappers have run, so
 * every name is camelCase and every decimal is a branded `Money`.
 *
 * Rules the whole file turns on, each checked against mm-be's `dev` branch
 * (`services/courses/`) and the live API (dev.mmitra.space, 2026-09-26):
 *
 *  1. HTML IS HTML. `description`, `studyMaterials`, a lecture's
 *     `description`/`learningMaterials` and a FAQ answer arrive as raw CMS
 *     markup — inline styles, `&nbsp;` padding, blank `<h2>&nbsp;</h2>`
 *     headings. They stay strings here and are only ever drawn through
 *     `features/courses/components/SafeHtml`. A blank string is `null`.
 *  2. `total_hours` IS 0 ON EVERY COURSE and is not mapped at all. A course's
 *     length is the sum of its lectures' `durationMinutes`
 *     (`features/courses/lib/duration.ts`); a card shows its lecture count.
 *  3. LENGTHS HAVE TWO UNITS. A lecture's `video_length` is MINUTES in every
 *     course on the wire (a 156-lecture course sums to 2,679 — 44.6 hours), so
 *     it is `durationMinutes`. A learning-path step's `video_length` is
 *     SECONDS — the table is v5's own, and mm-be seeds it as `minutes * 60` —
 *     so it is `durationSeconds`. mm-be's watch logic treats the lecture
 *     field as seconds too; see `courseService.saveWatch` for what that does.
 *  4. VIDEO FIELDS ARE PAYWALLED. The three wire fields are null on every
 *     lecture of a course the member does not own, unless the lecture is
 *     free. They collapse into one `source` (`lib/lecture-source.ts` says
 *     which wins and why): `null` is "nothing to play", never an error.
 *  5. NOT-FOUND IS A VALUE. `/path/` answers 404 `LEARNING_PATH_NOT_FOUND`
 *     until a path is authored; the course, step, term and certificate reads
 *     404 for an unknown id. The queries resolve each of those codes to
 *     `null` (`features/courses/api/queries.ts`), so none is an error page.
 *  6. FREE IS MM-BE'S WORD, NEVER THE PRICE. `isFree` comes from the course's
 *     `course_type` (`services/courses/pricing.py`): a Free course is joined
 *     with `enrol/`, any other is bought with `checkout/` at `discountPrice`.
 *     Staging has Paid courses priced 0.00 — not free, and not buyable
 *     either (400 `COURSE_NOT_PURCHASABLE`). `lib/price.ts` turns the fields
 *     into the one view every price and call to action reads. A LECTURE's
 *     `isFree` is another thing: a free preview inside a course that may well
 *     be paid.
 *  7. AN UNKNOWN FREE FLAG IS NEVER "FREE". A backend that predates
 *     `is_free` sends none, and every Learn read embeds this card, so a strict
 *     field would blank the whole section for want of it. It falls back to
 *     `false` instead: the price rule then decides ("Not on sale yet" when
 *     nothing is charged), and a wrong `false` corrects itself at Pay (400
 *     `COURSE_IS_FREE`).
 *  8. THE ACCESS PERIOD IS KEPT WITH ITS VERDICT. mm-be refuses a course whose
 *     `purchase_expires_on` is 0 or less, or above 36,500 days
 *     (`purchases._require_sane_access_period`), and reads a null one as
 *     "never lapses". The mapper therefore keeps the two apart: `accessDays`
 *     is the period when it is usable and null otherwise, `accessPeriodOk` says
 *     whether mm-be would accept it. A null `accessDays` is "never lapses" ONLY
 *     with `accessPeriodOk` true.
 */

// ── Primitives ───────────────────────────────────────────────────────────────

const idSchema = z.number().int().positive()
const countSchema = z.number().int().min(0)
const textSchema = z.string().min(1)
const optionalTextSchema = z.string().min(1).nullable()

/** CMS markup, blank already mapped to `null` — rule 1. */
const htmlSchema = z.string().min(1).nullable()

/**
 * An absolute http(s) URL. Thumbnails arrive absolute (mm-be joins its asset
 * host); a video or discussion field that is anything else — a relative path,
 * a pasted `<iframe>` — is dropped to `null` by the mapper rather than handed
 * to an `src`.
 */
export const httpUrlSchema = z.string().regex(/^https?:\/\/\S+$/i, 'Expected an http(s) URL')

// ── Vocabulary ───────────────────────────────────────────────────────────────

/**
 * A path step's state, exactly as mm-be's `objectives._status` computes it:
 * `done` (the member completed it), `current` (the lowest-ordered step not
 * done — at most one) and `locked` (everything after it). A signed-out read
 * has none done, so step 1 is `current`. The wire types it as a bare string;
 * a value outside these three is mapped to `null`, never parsed against the
 * closed set, so a new status cannot blank the page.
 */
export const PATH_STEP_STATUSES = ['done', 'current', 'locked'] as const
export const pathStepStatusSchema = z.enum(PATH_STEP_STATUSES)

/**
 * Where a path step sends the member once watched — mm-be stores a bare app
 * SECTION name (`deep_link`, 63 chars; seeded as these four), not a URL.
 * `lib/path.ts` maps each to its route. An unknown name maps to `null`.
 */
export const PATH_DEEP_LINKS = ['market', 'watchlist', 'portfolio', 'alerts'] as const
export const pathDeepLinkSchema = z.enum(PATH_DEEP_LINKS)

/**
 * What a player can play, decided once in the service (rule 4).
 *
 * - `embed` — a hosted player page (`embed_url`), drawn in an `<iframe>`.
 *   The host handles its own streaming and captions.
 * - `video` — a media URL for a `<video>` element: the asset host's file
 *   (`video`) or the stream URL (`video_url`). `hls` is true for an `.m3u8`
 *   playlist, which only Safari plays natively — a `<video>` elsewhere needs
 *   an HLS library, so a player must check it before choosing `<video>`.
 */
export const lectureSourceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('embed'), url: httpUrlSchema }),
  z.object({ kind: z.literal('video'), url: httpUrlSchema, hls: z.boolean() }),
])

// ── Catalog ──────────────────────────────────────────────────────────────────

/**
 * One course, as every list carries it: the catalog, the home shelves,
 * popular, and nested in every progress and certificate row.
 *
 * `price` is the list price and `discountPrice` what is CHARGED — mm-be's
 * `pricing.price_for` reads `discount_price` and never `price`. It may EQUAL
 * `price` (no discount), and is 0.00 on a course nobody has priced yet.
 * `lib/price.ts` decides free / not on sale / discounted / plain from these
 * two and `isFree`; never compare them by hand.
 *
 * `accessDays` is the course's `purchase_expires_on`: how many days a
 * purchase stays valid from the day it is made (90 / 180 / 270 on dev; mm-be
 * seeds 365). Null means no limit was set — "never lapses" — when
 * `accessPeriodOk` is true, and "nothing usable to say" when it is false
 * (rule 8). The member's OWN remaining days are `MyLearningCourse.daysLeft`,
 * computed from their purchase — never derive those from this.
 *
 * `slug` is not mapped: no route takes it, and the wire's values
 * (`"the-traders-mind:-unlocking-…"`) are not safe path segments anyway.
 */
export const courseCardSchema = z.object({
  id: idSchema,
  /** Rule 6: enrol, don't pay. NOT a lecture's `isFree` (a free preview). Rule 7: unknown is false. */
  isFree: z.boolean().catch(false),
  title: textSchema,
  /** As sent — "Beginner", "Intermediary" (sic). Null when blank. */
  level: optionalTextSchema,
  language: optionalTextSchema,
  /** Completing the course earns a certificate. */
  hasCertificate: z.boolean(),
  thumbnail: httpUrlSchema.nullable(),
  price: moneySchema,
  discountPrice: moneySchema,
  accessDays: z.number().int().positive().nullable(),
  /** Whether mm-be would sell or enrol this course with the period it carries — rule 8. */
  accessPeriodOk: z.boolean(),
  /** Active lectures, counted by mm-be (not the stale stored column). */
  lectureCount: countSchema,
})

export const instructorSchema = z.object({
  id: idSchema,
  name: textSchema,
  designation: optionalTextSchema,
  /** Free text or CMS markup — draw through SafeHtml either way. */
  description: htmlSchema,
})

/**
 * One lecture of a course detail.
 *
 * `source` is `null` for a locked lecture (not owned, not free) and for an
 * owned one whose video was never uploaded; `lib/lecture-access.ts` tells
 * the two apart for a screen.
 *
 * `started` is the wire's `watched`, renamed for what mm-be actually sends:
 * the member has saved a watch position on this lecture — ANY position
 * (`repository.get_watched_lecture_ids` does not filter on the "watched to
 * the end" flag). It is not "finished". `completed` is the member's own
 * "Mark complete" toggle, a separate record.
 */
export const lectureSchema = z.object({
  id: idSchema,
  title: textSchema,
  description: htmlSchema,
  thumbnail: httpUrlSchema.nullable(),
  durationMinutes: countSchema,
  /** The curriculum section this lecture sits under (`sub_course_title`). */
  section: optionalTextSchema,
  discussionUrl: httpUrlSchema.nullable(),
  learningMaterials: htmlSchema,
  source: lectureSourceSchema.nullable(),
  isFree: z.boolean(),
  completed: z.boolean(),
  started: z.boolean(),
})

/**
 * A course's own page. Public, but member-aware: `purchased` and every
 * lecture's `completed`/`started` are the signed-in member's.
 *
 * `curriculum` (HTML) is not mapped: it restates the lecture list as prose
 * (placeholder text on some courses — "Smart Money Concepts(SMC)
 * curriculum"), and the curriculum is drawn from `lectures`, grouped by
 * `section` (`lib/curriculum.ts`).
 *
 * `ratingAverage` is null when no review carries a rating — never 0.
 */
export const courseDetailSchema = courseCardSchema.extend({
  description: htmlSchema,
  studyMaterials: htmlSchema,
  instructor: instructorSchema.nullable(),
  purchased: z.boolean(),
  ratingAverage: moneySchema.nullable(),
  ratingCount: countSchema,
  lectures: z.array(lectureSchema),
})

/** A lecture anyone may watch, with the course it belongs to — `/home/`'s free shelf. */
export const freeLectureSchema = z.object({
  id: idSchema,
  title: textSchema,
  thumbnail: httpUrlSchema.nullable(),
  durationMinutes: countSchema,
  courseId: idSchema,
  courseTitle: textSchema,
  source: lectureSourceSchema.nullable(),
})

/**
 * One review. `rating` is null for a text-only review (shown, but not
 * counted in the average); `reviewerName` is null for an anonymous one.
 */
export const courseReviewSchema = z.object({
  id: idSchema,
  rating: z.number().int().nullable(),
  text: z.string(),
  reviewerName: optionalTextSchema,
  createdAt: isoDateTimeSchema,
})

/** One entry of the global FAQ. `answer` may be plain text or CMS markup. */
export const faqSchema = z.object({
  id: idSchema,
  question: textSchema,
  answer: z.string(),
})

// ── The member's own ─────────────────────────────────────────────────────────

/**
 * One of the member's active purchases with its progress.
 *
 * `lecturesWatched` counts lectures watched to the END (mm-be's "watched"
 * flag, set when a saved position is within 5s of the lecture's length) —
 * a stricter measure than a lecture's `started`. `daysLeft` is null when the
 * purchase never expires, and 0 on its last day.
 */
export const myLearningCourseSchema = z.object({
  course: courseCardSchema,
  lecturesWatched: countSchema,
  lecturesTotal: countSchema,
  finished: z.boolean(),
  daysLeft: countSchema.nullable(),
})

export const myLearningSchema = z.object({
  summary: z.object({
    coursesTotal: countSchema,
    inProgress: countSchema,
    finished: countSchema,
    lecturesWatched: countSchema,
    lecturesTotal: countSchema,
  }),
  courses: z.array(myLearningCourseSchema),
})

/** An active (not expired) purchase. `expiresAt` null: no expiry. */
export const purchasedCourseSchema = z.object({
  id: idSchema,
  course: courseCardSchema,
  purchasedAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema.nullable(),
  certificateIssuedOn: isoDateSchema.nullable(),
})

/**
 * An issued certificate. Kept on the account after the course's access
 * lapses. `code` is null only for a certificate issued before codes existed;
 * without one there is nothing to download or verify.
 */
export const certificateSchema = z.object({
  course: courseCardSchema,
  issuedOn: isoDateSchema,
  code: textSchema.nullable(),
})

/**
 * The Certificates screen: what is issued, and `closeToOne` — courses the
 * member has started (at least one lecture watched to the end) but not
 * finished.
 */
export const certificatesSchema = z.object({
  issued: z.array(certificateSchema),
  closeToOne: z.array(myLearningCourseSchema),
})

/**
 * A certificate confirmed against mm-be's records — the PUBLIC read. The
 * holder's name is masked by mm-be ("Sandip K."), null when none is on file.
 */
export const verifiedCertificateSchema = z.object({
  code: textSchema,
  courseTitle: textSchema,
  issuedOn: isoDateSchema,
  holderName: optionalTextSchema,
})

/**
 * `/home/`'s shelves. `resume` is the member's started-but-unfinished
 * courses (empty signed out); `startHere` is the Beginner courses and
 * `goingDeeper` everything past them.
 */
export const courseHomeSchema = z.object({
  resume: z.array(myLearningCourseSchema),
  freeToWatch: z.array(freeLectureSchema),
  startHere: z.array(courseCardSchema),
  goingDeeper: z.array(courseCardSchema),
})

// ── Learning path ────────────────────────────────────────────────────────────

export const pathStepSchema = z.object({
  id: idSchema,
  order: z.number().int(),
  title: textSchema,
  subtitle: optionalTextSchema,
  durationSeconds: countSchema,
  status: pathStepStatusSchema.nullable(),
  deepLink: pathDeepLinkSchema.nullable(),
})

/** The one active path, member-aware (`status`, `completed`, `showOnHome`). */
export const learningPathSchema = z.object({
  slug: textSchema,
  title: textSchema,
  tagline: optionalTextSchema,
  total: countSchema,
  completed: countSchema,
  /** The member's "show this path on Home" preference; true signed out. */
  showOnHome: z.boolean(),
  steps: z.array(pathStepSchema),
})

/**
 * One step's page. `glossaryTerms` are DISPLAY words ("Kitta", "NEPSE"),
 * not dictionary slugs — link them to the dictionary's search, not to a term
 * page. `source` is only ever a `video` (a step has no embed field).
 */
export const pathStepDetailSchema = z.object({
  id: idSchema,
  order: z.number().int(),
  title: textSchema,
  subtitle: optionalTextSchema,
  source: lectureSourceSchema.nullable(),
  durationSeconds: countSchema,
  learnPoints: z.array(textSchema),
  glossaryTerms: z.array(textSchema),
  completed: z.boolean(),
  /** How many steps the path has — "Step 3 of 7". */
  total: countSchema,
  nextStepId: idSchema.nullable(),
  nextStepTitle: optionalTextSchema,
})

// ── Dictionary ───────────────────────────────────────────────────────────────

export const dictionaryTermCardSchema = z.object({
  slug: textSchema,
  term: textSchema,
  shortDef: z.string(),
})

/**
 * One term. `example` is a real listed company with its LIVE last-traded
 * price (null when none is recorded — never 0); `symbol` is null when the
 * company row has none, and then there is no stock page to link to.
 */
export const dictionaryTermSchema = z.object({
  slug: textSchema,
  term: textSchema,
  termNepali: optionalTextSchema,
  shortDef: z.string(),
  oneLine: z.string(),
  facts: z.array(z.object({ label: z.string(), value: z.string() })),
  example: z
    .object({
      symbol: optionalTextSchema,
      name: textSchema,
      price: moneySchema.nullable(),
    })
    .nullable(),
  related: z.array(z.object({ slug: textSchema, term: textSchema })),
})

// ── Paging ───────────────────────────────────────────────────────────────────

/**
 * One server page of a list. `total` is the grand total (the envelope's
 * `meta.total`, NOT `meta.count`, which is this page's row count), and
 * `totalPages` is 0 for an empty list.
 */
const pageOf = <Row extends z.ZodType>(row: Row) =>
  z.object({
    rows: z.array(row),
    page: z.number().int().min(1),
    pageSize: z.number().int().min(1),
    total: countSchema,
    totalPages: countSchema,
  })

export const courseReviewsPageSchema = pageOf(courseReviewSchema)
export const faqPageSchema = pageOf(faqSchema)
export const dictionaryPageSchema = pageOf(dictionaryTermCardSchema)

// ── Writes ───────────────────────────────────────────────────────────────────

/** The member's lecture completion, as the server settled it. */
export const lectureCompletionSchema = z.object({
  lectureId: idSchema,
  completed: z.boolean(),
})

/**
 * A saved watch position's outcome: `watched` is true once mm-be counts the
 * lecture watched to the end (and may have issued a certificate).
 */
export const watchProgressSchema = z.object({
  lectureId: idSchema,
  watched: z.boolean(),
})

export const stepCompletionSchema = z.object({
  stepId: idSchema,
  completed: z.boolean(),
})

export const learningPrefsSchema = z.object({
  showOnHome: z.boolean(),
})

// ── Buying a course ──────────────────────────────────────────────────────────

/**
 * The 201 from `courses/{id}/checkout/`: the subscription's checkout start
 * minus its `discount` — mm-be refuses codes on courses on purpose.
 * `transactionId` is what `confirm` wants once the gateway sends the member
 * back; `amount` is what will be charged, the course's `discountPrice` as
 * mm-be read it from the database. The hand-off is the one every product
 * shares (`schemas/payments.ts`), so a course never has its own reading of
 * where the browser goes next.
 */
export const courseCheckoutStartSchema = z.object({
  transactionId: z.string().min(1),
  amount: moneySchema,
  gateway: z.string(),
  handoff: gatewayHandoffSchema,
})

/**
 * The 200 from `courses/checkout/confirm/` — the payment is verified and the
 * course granted. A cosmetic field that fails its shape (`courseTitle`,
 * `alreadyCompleted`) is caught to a fallback rather than turning a settled
 * payment into an error. Two answers are strict because no fallback would be
 * honest:
 *
 *  - `expiresOn` null is "access never lapses", a real state, so an
 *    unreadable date must not be allowed to become it.
 *  - `accessActive` is "can they watch it now". A replayed callback finds it
 *    false when the grant has been revoked or has gone missing, and
 *    `expiresOn` is null then too — read `accessActive` first, never a null
 *    `expiresOn` alone.
 *
 * `alreadyCompleted` true is the same success (a refreshed return page, a
 * replayed callback, the sweeper got there first), never a second purchase.
 */
export const coursePurchaseResultSchema = z.object({
  transactionId: z.string().min(1),
  courseId: idSchema,
  courseTitle: z.string().catch(''),
  expiresOn: isoDateTimeSchema.nullable(),
  accessActive: z.boolean(),
  alreadyCompleted: z.boolean().catch(false),
})

/**
 * The 201 from `courses/{id}/enrol/` — a free course joined, no payment row.
 * Idempotent: `alreadyEnrolled` true is the same success. `expiresOn` null is
 * "never lapses", so it is strict, as on the purchase result.
 */
export const courseEnrolmentSchema = z.object({
  courseId: idSchema,
  courseTitle: z.string().catch(''),
  expiresOn: isoDateTimeSchema.nullable(),
  alreadyEnrolled: z.boolean().catch(false),
})

/**
 * `payments[].status`, Title Case on the wire — the three words v5 itself
 * writes. The service narrows any other word (a legacy row) to `null` before
 * the schema runs (`knownWord`), so a new word costs a chip, not the list.
 */
export const COURSE_PAYMENT_STATUSES = ['Pending', 'Completed', 'Cancelled'] as const
export const coursePaymentStatusSchema = z.enum(COURSE_PAYMENT_STATUSES)

/**
 * One attempt from `GET courses/payments/` — newest first, abandoned
 * (`Pending`) and `Cancelled` attempts included, because "did that go
 * through?" is what the list answers. `courseId` null with a blank
 * `courseTitle` is an attempt whose course row is gone; `amount` null is a
 * legacy row with none recorded. `method` is the legacy spelling ("Esewa"),
 * not the gateway id. `status` null is a word this build does not know —
 * neither paid nor failed. `completedOn` is the UTC date.
 */
export const coursePaymentRecordSchema = z.object({
  transactionId: z.string().min(1),
  courseId: idSchema.nullable(),
  courseTitle: z.string(),
  amount: moneySchema.nullable(),
  method: z.string().nullable(),
  status: coursePaymentStatusSchema.nullable(),
  startedOn: isoDateTimeSchema,
  completedOn: isoDateSchema.nullable(),
})

/** Lenient: one malformed legacy row is dropped, not fatal to the history. */
export const coursePaymentListSchema = lenientList(coursePaymentRecordSchema, 'course payment')

// ── What the reads take ──────────────────────────────────────────────────────

/** A page of a server-paged list. mm-be caps `pageSize` at 200. */
export const pageQuerySchema = z.object({
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(200),
})

/**
 * The dictionary's search. `q` is trimmed and `null` when empty — the one
 * normal form the loader and the screen both build the key from, so `?q=`
 * absent, `q=""` and `q="  "` are one cache entry.
 */
export const dictionaryQuerySchema = pageQuerySchema.extend({
  q: z.string().min(1).nullable(),
})

export type PathStepStatus = z.infer<typeof pathStepStatusSchema>
export type PathDeepLink = z.infer<typeof pathDeepLinkSchema>
export type LectureSource = z.infer<typeof lectureSourceSchema>
export type CourseCard = z.infer<typeof courseCardSchema>
export type Instructor = z.infer<typeof instructorSchema>
export type Lecture = z.infer<typeof lectureSchema>
export type CourseDetail = z.infer<typeof courseDetailSchema>
export type FreeLecture = z.infer<typeof freeLectureSchema>
export type CourseReview = z.infer<typeof courseReviewSchema>
export type Faq = z.infer<typeof faqSchema>
export type MyLearningCourse = z.infer<typeof myLearningCourseSchema>
export type MyLearning = z.infer<typeof myLearningSchema>
export type PurchasedCourse = z.infer<typeof purchasedCourseSchema>
export type Certificate = z.infer<typeof certificateSchema>
export type Certificates = z.infer<typeof certificatesSchema>
export type VerifiedCertificate = z.infer<typeof verifiedCertificateSchema>
export type CourseHome = z.infer<typeof courseHomeSchema>
export type PathStep = z.infer<typeof pathStepSchema>
export type LearningPath = z.infer<typeof learningPathSchema>
export type PathStepDetail = z.infer<typeof pathStepDetailSchema>
export type DictionaryTermCard = z.infer<typeof dictionaryTermCardSchema>
export type DictionaryTerm = z.infer<typeof dictionaryTermSchema>
export type CourseReviewsPage = z.infer<typeof courseReviewsPageSchema>
export type FaqPage = z.infer<typeof faqPageSchema>
export type DictionaryPage = z.infer<typeof dictionaryPageSchema>
export type LectureCompletion = z.infer<typeof lectureCompletionSchema>
export type WatchProgress = z.infer<typeof watchProgressSchema>
export type StepCompletion = z.infer<typeof stepCompletionSchema>
export type LearningPrefs = z.infer<typeof learningPrefsSchema>
export type CourseCheckoutStart = z.infer<typeof courseCheckoutStartSchema>
export type CoursePurchaseResult = z.infer<typeof coursePurchaseResultSchema>
export type CourseEnrolment = z.infer<typeof courseEnrolmentSchema>
export type CoursePaymentStatus = z.infer<typeof coursePaymentStatusSchema>
export type CoursePaymentRecord = z.infer<typeof coursePaymentRecordSchema>
export type PageQuery = z.infer<typeof pageQuerySchema>
export type DictionaryQuery = z.infer<typeof dictionaryQuerySchema>
